/**
 * The on-screen preview.
 *
 * THE WHOLE PROBLEM WITH A PREVIEW IS THAT IT IS A LIE BY DEFAULT
 * --------------------------------------------------------------
 * The obvious way to build a preview is to re-implement the document in React
 * with Tailwind, which produces something that looks approximately right on
 * screen and bears no relationship to what gets printed. Then the user zooms
 * in, sees a defect the preview never showed, and prints an invoice with
 * misaligned columns. A preview that has silently diverged is worse than no
 * preview at all, because it transfers confidence it has not earned.
 *
 * So this file renders the PRINT document, not a description of it:
 *
 *   - the same `renderA4Body()` markup, byte for byte;
 *   - the same `a4Stylesheet()` bytes, in a SEPARATE `<style>` element, so
 *     "the preview and the print are styled identically" is checkable rather
 *     than asserted;
 *   - a preview stylesheet that can only ever touch `pv-` classes.
 *
 * The `pv-` prefix is the enforcement mechanism. It is not a naming
 * convention - it is a testable one: `preview.test.ts` asserts the preview
 * stylesheet contains no selector that could reach a `doc-` class, so the
 * preview provably cannot restyle the document however it is written later.
 *
 * Pure: a string in, a string out. No DOM, no clock, no measurement. The
 * numbers a caller needs to measure are computed by the exported pure
 * functions below, and the DOM work lives in `platform/`.
 */
import { A4_HEIGHT_MM, A4_WIDTH_MM } from './geometry';
import { a4Stylesheet } from './a4Stylesheet';
import { esc, renderA4Body, type A4RenderOptions, type BusinessIdentity } from './renderA4';
import { modeClass } from './renderMode';
import type { NormalizedDocument } from './types';

/** CSS defines 1in as exactly 96px, so this converts millimetres to px. */
export const MM_TO_PX = 96 / 25.4;

/** A4 in px at scale 1. */
export const A4_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX;
export const A4_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX;

/** Zoom bounds. Below 25% nothing is legible; above 200% nobody can see a page. */
export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 2;

/**
 * The scale that fits an A4 page into `availablePx` of width.
 *
 * Pure, and therefore testable without a viewport. It never returns 0 or a
 * negative number, because a zero scale collapses the page to nothing and the
 * user is left with a blank panel and no error.
 */
export function fitScale(availablePx: number): number {
  if (!Number.isFinite(availablePx) || availablePx <= 0) return MIN_ZOOM;
  return clampZoom(availablePx / A4_WIDTH_PX);
}

/** Keep a zoom factor inside the legible range. */
export function clampZoom(scale: number): number {
  if (!Number.isFinite(scale)) return 1;
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale));
}

/**
 * How many sheets a document will print on.
 *
 * The preview's most useful job is not drawing the page - it is telling the
 * user BEFORE they print that their 40-line invoice is going to come out on two
 * sheets. That number is invisible otherwise, and discovering it from the
 * printer is the worst possible time.
 *
 * The caller measures the content height and passes it in; this stays pure.
 * A hair of tolerance avoids reporting 2 pages for content that overflows by a
 * fraction of a pixel from a rounding artefact.
 */

export interface PreviewOptions extends A4RenderOptions {
  /** Display zoom. Defaults to fitting the available width. */
  readonly scale?: number;
  /** The width to fit into, in px. Only used when `scale` is absent. */
  readonly fitWidthPx?: number;
}

/**
 * Preview-only CSS.
 *
 * Every selector is `pv-`-scoped and every one of them is about the SCREEN:
 * a desk colour, a page edge, and the scale transform. None of them describes
 * the document, which is the entire point - a preview rule that said anything
 * about `.doc-page` internals would be a second styling authority, and that is
 * what broke 1.6.x.
 */
export function previewStylesheet(scale: number): string {
  return `
html { background: #6b7280; }
body { background: #6b7280; margin: 0; padding: 6mm 0; }
/* The desk. Nothing here describes the document. */
.pv-frame {
  margin: 0 auto;
  width: ${A4_WIDTH_PX * scale}px;
  height: ${A4_HEIGHT_PX * scale}px;
  position: relative;
}
/* Scale the page, not the frame, so the shadow stays 1px crisp. */
.pv-page {
  transform: scale(${scale});
  transform-origin: top left;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
}
/* Where the NEXT sheet begins, so an overflow is visible rather than inferred. */
.pv-page-overflow { outline: 2px dashed #dc2626; outline-offset: 2px; }
@media print {
  html, body { background: #fff; }
  .pv-frame { width: auto; height: auto; }
  .pv-page { transform: none; box-shadow: none; }
}
`.trim();
}

/**
 * Render the on-screen preview.
 *
 * The `<style>` holding the document rules is emitted SEPARATELY and is
 * byte-identical to the one the print path emits, so the two are provably
 * styled by the same CSS rather than by two copies that happened to agree on
 * the day they were written.
 */
export function renderPreview(
  m: NormalizedDocument,
  biz: BusinessIdentity,
  opt: PreviewOptions = {},
  assetsBaseUrl = '',
): string {
  const scale = clampZoom(opt.scale ?? fitScale(opt.fitWidthPx ?? A4_WIDTH_PX));
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<title>${esc(m.docNumber)} — preview</title>
<style id="doc">${a4Stylesheet(assetsBaseUrl)}</style>
<style id="pv">${previewStylesheet(scale)}</style>
</head>
<body>
<div class="pv-frame">
<div class="pv-page">
<div class="doc-page ${modeClass(m.docType)}">
  ${renderA4Body(m, biz, opt)}
</div>
</div>
</div>
</body>
</html>`;
}

export function pagesFor(contentHeightPx: number, pageHeightPx: number = A4_HEIGHT_PX): number {
  if (!Number.isFinite(contentHeightPx) || contentHeightPx <= 0) return 1;
  if (!Number.isFinite(pageHeightPx) || pageHeightPx <= 0) return 1;
  const TOLERANCE = 0.5;
  return Math.max(1, Math.ceil((contentHeightPx - TOLERANCE) / pageHeightPx));
}